import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {FromServerRunOutputSamplesTable} from 'lab-automation/output-samples/RunOutputSamplesTableConstants';
import waitForEntrySaved from 'lab-automation/utils/waitForEntrySaved';
import {FromServerResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';

import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import * as AutomationFileDAO from './AutomationFileDAO';
import {AutomationOutputFileActionType} from './LabAutomationConstants';

export const SKIP_ROWS_WITH_ERRORS_TOOLTIP =
  'If enabled, processing will continue even if there are errors. You can view all successful and failed rows after processing is complete. Note that this option is required to use file chunking when processing.';
export const SKIP_ROWS_WITH_ERRORS_LABEL = 'Skip rows with errors';

const CHECKBOX_SIZE_PX = 14;
const CHECKMARK_SIZE_PX = 10;

const exportDisplayStyles: {
  root: React.CSSProperties;
  label: React.CSSProperties;
  checkmark: React.CSSProperties;
} = {
  root: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  label: {
    paddingLeft: 6,
    lineHeight: '16px',
    fontWeight: 'normal',
    fontSize: 13,
  },
  checkmark: {
    color: Color.WHITE,
  },
};

function getExportCheckboxBoxStyle(checked: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: CHECKBOX_SIZE_PX,
    height: CHECKBOX_SIZE_PX,
    marginTop: 2,
    flexShrink: 0,
    backgroundColor: checked ? Color.BLUE_500 : Color.WHITE,
    border: `1px solid ${checked ? Color.BLUE_500 : Color.GRAY_300}`,
    borderRadius: 2,
  };
}

interface SkipRowsWithErrorsInputProps {
  automationOutputFile: AutomationOutputFileGraphQL;
  updateAutomationOutputFile: (
    automationOutputFile: AutomationOutputFileGraphQL,
    structuredTables?: Array<FromServerRunOutputSamplesTable | FromServerResultsTable>
  ) => void;
}
interface ConnectedProps {
  outputFileStatus: AutomationFileStatusGraphQL | null;
}

class SkipRowsWithErrorsInputConnector extends DataConnector<SkipRowsWithErrorsInputProps, ConnectedProps> {
  enhanceProps(): ConnectedProps {
    return {
      outputFileStatus: this.props.automationOutputFile.id
        ? this.reduxState().ui.automationFileStatus[this.props.automationOutputFile.id]
        : null,
    };
  }
}

function getCheckboxLabel(automationOutputFile: AutomationOutputFileGraphQL): string {
  if (BenchlingConfig.LAB_AUTOMATION_CASCADE_OUTPUT_FILE_ERRORS_TO_RELATED_ROWS) {
    const actionType = automationOutputFile.automationOutputFileConfig.automationOutputFileActionType;
    if (
      actionType === AutomationOutputFileActionType.PURE_TRANSFER ||
      actionType === AutomationOutputFileActionType.TRANSFORMATION_TRANSFER
    ) {
      return 'Skip destinations with errors';
    }
  }
  return SKIP_ROWS_WITH_ERRORS_LABEL;
}

function SkipRowsWithErrorsExportDisplay({checked, label}: {checked: boolean; label: string}): JSX.Element {
  return (
    <div role="checkbox" aria-checked={checked} aria-readonly={true} style={exportDisplayStyles.root}>
      <span
        aria-hidden={true}
        style={getExportCheckboxBoxStyle(checked)}
        {...getTestProps({component: 'SkipRowsWithErrorsExportDisplay', element: 'box'})}
      >
        {checked && (
          <svg
            width={CHECKMARK_SIZE_PX}
            height={CHECKMARK_SIZE_PX}
            viewBox="0 0 10 7"
            style={exportDisplayStyles.checkmark}
            {...getTestProps({component: 'SkipRowsWithErrorsExportDisplay', element: 'checkmark'})}
          >
            <path
              d="M4 4.586L1.707 2.293A1 1 0 1 0 .293 3.707l3 3a1 1 0 0 0 1.414 0l5-5A1 1 0 1 0 8.293.293L4 4.586z"
              fill="currentColor"
              fillRule="evenodd"
              clipRule="evenodd"
            />
          </svg>
        )}
      </span>
      <span style={exportDisplayStyles.label}>{label}</span>
    </div>
  );
}

const SkipRowsWithErrorsInput: React.FunctionComponent<SkipRowsWithErrorsInputProps> = (
  props: SkipRowsWithErrorsInputProps & ConnectedProps
): JSX.Element => {
  const [checked, setChecked] = useState(
    props.automationOutputFile?.skipRowsWithErrorsRequired ||
      props.automationOutputFile.skipRowsWithErrors ||
      false
  );
  const label = getCheckboxLabel(props.automationOutputFile);

  return (
    <div className={css(skipRowsStyles.showErrorsCheckboxRow)}>
      <EditorPropertiesContext.Consumer>
        {({fileId, isExportView}) =>
          isExportView ? (
            <SkipRowsWithErrorsExportDisplay checked={checked} label={label} />
          ) : (
            <>
              <CheckboxInput
                id={`skip-rows-with-errors-${props.automationOutputFile.id}`}
                className={css(skipRowsStyles.showErrorsCheckbox)}
                // If a file is RUNNING, its frontend status is only in the Redux store, not in the backend.
                // If its SUCCEEDED or FAILED, its status is reflective of the backend status.
                disabled={
                  props.outputFileStatus === AutomationFileStatusGraphQL.running ||
                  props.automationOutputFile.status !== AutomationFileStatusGraphQL.pending ||
                  props.automationOutputFile?.skipRowsWithErrorsRequired
                }
                label={label}
                checked={checked}
                onChange={async (e: {target: {value: string}}) => {
                  const skipRowsWithErrors = !checked;
                  setChecked(skipRowsWithErrors);
                  const saved = fileId != null ? await waitForEntrySaved(fileId) : true;
                  if (saved) {
                    const response = await AutomationFileDAO.updateAutomationOutputFileParams(
                      props.automationOutputFile.id,
                      {skipRowsWithErrors}
                    );
                    props.updateAutomationOutputFile(response.automationOutputFile);
                  }
                }}
              />
              <Tooltip title={SKIP_ROWS_WITH_ERRORS_TOOLTIP}>
                <BenchlingIcon className={css(skipRowsStyles.infoIcon)} icon="more-info" />
              </Tooltip>
            </>
          )
        }
      </EditorPropertiesContext.Consumer>
    </div>
  );
};

export const skipRowsStyles = StyleSheet.create({
  showErrorsCheckbox: {
    alignItems: 'center',
  },
  showErrorsCheckboxRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '5px',
  },
  infoIcon: {
    marginLeft: '4px',
    color: Color.GRAY_300,
  },
});

export default SkipRowsWithErrorsInputConnector.connect(SkipRowsWithErrorsInput);
